import React, { useEffect, useState } from "react";
import { trackHome } from "@/lib/requestContext";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Star, ShieldCheck, MapPin, Globe, Sparkles, Award, MessageSquare, ArrowLeft, Briefcase, Languages, CheckCircle2 } from "lucide-react";
import PublicLayout from "@/layouts/PublicLayout";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { SpanishBadge, speaksSpanish } from "@/components/marketplace/SpanishBadge";
import api from "@/lib/api";
import ReviewCard from "@/components/marketplace/ReviewCard";
import ServiceCard from "@/components/marketplace/ServiceCard";
import WebReviews from "@/components/marketplace/WebReviews";
import FavoriteButton from "@/components/marketplace/FavoriteButton";
import TemplateBands from "@/components/marketplace/TemplateBands";
import { useDocumentTitle } from "@/lib/useDocumentTitle";
import { useAuth } from "@/contexts/AuthContext";

export default function ProviderProfilePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [provider, setProvider] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [categoryMap, setCategoryMap] = useState({});
  const [locationMap, setLocationMap] = useState({});
  const [reviews, setReviews] = useState([]);
  const [webReviews, setWebReviews] = useState([]);
  const [services, setServices] = useState([]);
  const [messageBusy, setMessageBusy] = useState(false);
  useDocumentTitle(provider?.display_name || (notFound ? "Provider not found" : "Provider"));

  const openMessage = async () => {
    if (!provider) return;
    if (!user) {
      const next = encodeURIComponent(location.pathname + "?action=message");
      navigate(`/login?next=${next}`);
      return;
    }
    setMessageBusy(true);
    try {
      const { data } = await api.post("/messages/direct", { to_provider_id: provider.id });
      navigate(`/dashboard/messages?conv=${data.conversation_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.error || "Could not open the conversation");
    } finally {
      setMessageBusy(false);
    }
  };

  const requestQuote = () => {
    if (!provider) return;
    if (!user) {
      const next = encodeURIComponent(`/request-a-service?invite=${provider.id}`);
      navigate(`/login?next=${next}`);
      return;
    }
    navigate(`/request-a-service?invite=${provider.id}`);
  };

  // Resume-action: if we returned from /login with ?action=message, auto-open.
  useEffect(() => {
    if (!provider || !user) return;
    const params = new URLSearchParams(location.search);
    if (params.get("action") === "message") {
      openMessage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider, user]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/providers/${slug}`);
        setProvider(data);
        trackHome("provider_view", { provider: data?.id });
      } catch (_) {
        setNotFound(true);
      }
    })();
  }, [slug]);

  useEffect(() => {
    if (!provider?.id) return;
    (async () => {
      try {
        const { data } = await api.get(`/reviews/provider/${provider.id}`);
        setReviews(data.items || []);
      } catch (_) { /* ignore */ }
      try {
        const { data } = await api.get(`/reviews/external`, { params: { provider_id: provider.id } });
        setWebReviews(data.items || []);
      } catch (_) { /* ignore */ }
      try {
        const { data } = await api.get(`/services`, { params: { provider_id: provider.id, limit: 24 } });
        setServices(data.items || []);
      } catch (_) { /* ignore */ }
    })();
  }, [provider?.id]);

  useEffect(() => {
    if (!provider) return;
    (async () => {
      // Best-effort lookups for pretty display (dictionaries are tiny)
      try {
        const cats = await api.get("/categories");
        const ids = [...(provider.location_ids || []), provider.primary_location_id].filter(Boolean);
        const locs = ids.length ? await api.get("/locations", { params: { ids: ids.join(",") } }) : { data: [] };
        setCategoryMap(Object.fromEntries(cats.data.map((c) => [c.id, c])));
        setLocationMap(Object.fromEntries(locs.data.map((l) => [l.id, l])));
      } catch (_) { /* ignore */ }
    })();
  }, [provider]);

  if (notFound) {
    return (
      <PublicLayout>
        <div className="mx-auto max-w-3xl px-6 py-24 text-center">
          <h1 className="font-display text-3xl text-[#071b22]">Provider not found.</h1>
          <Link to="/providers" className="mt-6 inline-flex items-center gap-2 text-[#165a71] font-medium">
            <ArrowLeft className="h-4 w-4" /> Back to directory
          </Link>
        </div>
      </PublicLayout>
    );
  }

  if (!provider) {
    return (
      <PublicLayout>
        <div className="mx-auto max-w-3xl px-6 py-24 text-[#252A34]/60">Loading profile…</div>
      </PublicLayout>
    );
  }

  const initials = (provider.business_name || provider.display_name || "GC")
    .split(" ").filter(Boolean).map((p) => p[0]).slice(0, 2).join("");

  const verified = provider.verifications?.gcw_verified?.status === "approved";

  return (
    <PublicLayout>
      <TemplateBands entity="providers" slot="header" />
      {/* Cover */}
      <div className="relative h-56 md:h-72 w-full overflow-hidden" data-testid="provider-cover">
        {provider.cover_image ? (
          <img src={provider.cover_image} alt="" className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 bg-[#165a71]" />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-[#071b22]/50 via-[#071b22]/20 to-[#f8fafc]" />
      </div>

      <div className="mx-auto max-w-6xl px-6 lg:px-10 -mt-16 relative z-10">
        <div className="rounded-3xl bg-white border border-slate-200 p-6 md:p-8 shadow-[0_20px_60px_rgba(7,27,34,0.08)]">
          <div className="flex flex-col md:flex-row md:items-end gap-6">
            <Avatar className="h-24 w-24 md:h-28 md:w-28 rounded-3xl ring-4 ring-white shadow-lg">
              <AvatarImage src={provider.profile_image || undefined} alt={provider.display_name} />
              <AvatarFallback className="rounded-3xl bg-[#165a71] text-white font-display text-2xl">{initials}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 translate="no" className="font-display text-3xl md:text-4xl font-semibold text-[#071b22] tracking-tight" data-testid="provider-name">
                  {provider.business_name || provider.display_name}
                </h1>
                {provider.is_featured && <Sparkles className="h-5 w-5 text-amber-500" />}
                {provider.is_claimed === false && <Link to={`/claim/${provider.id}`} className="ml-2 align-middle inline-flex items-center rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-800 hover:bg-amber-100" data-testid="unclaimed-badge">Unclaimed business · Claim this business</Link>}
                {verified && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 text-xs font-medium text-emerald-800">
                    <ShieldCheck className="h-3.5 w-3.5" /> Verified
                  </span>
                )}
                {speaksSpanish(provider.languages) && <SpanishBadge />}
              </div>
              <p translate="no" className="mt-1.5 text-[#252A34]/70">{provider.tagline}</p>
              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
                <span className="inline-flex items-center gap-1.5 text-amber-600 font-medium">
                  <Star className="h-4 w-4 fill-amber-500 text-amber-500" />
                  {(provider.rating_avg || 0).toFixed(1)}
                  <span className="text-[#252A34]/60">({provider.rating_count || 0} reviews)</span>
                </span>
                <span className="text-[#252A34]/40">·</span>
                <span className="text-[#252A34]/75 inline-flex items-center gap-1"><Briefcase className="h-4 w-4" /> {provider.completed_jobs || 0} jobs</span>
                <span className="text-[#252A34]/40">·</span>
                <span className="text-[#252A34]/75 inline-flex items-center gap-1"><Languages className="h-4 w-4" /> {(provider.languages || []).join(", ")}</span>
                {provider.remote_available && (
                  <>
                    <span className="text-[#252A34]/40">·</span>
                    <span className="text-[#252A34]/75 inline-flex items-center gap-1"><Globe className="h-4 w-4" /> Remote</span>
                  </>
                )}
              </div>
            </div>
            <div className="flex flex-col md:flex-row gap-2 md:gap-3 shrink-0 items-center">
              <FavoriteButton kind="provider" targetId={provider.id} initial={!!provider.is_favorite} size="lg" />
              <button
                onClick={openMessage}
                disabled={messageBusy}
                className="rounded-full bg-[#165a71] text-white px-5 py-2.5 font-medium hover:bg-[#11485a] transition-colors inline-flex items-center gap-2 disabled:opacity-60"
                data-testid="provider-message"
              >
                <MessageSquare className="h-4 w-4" /> {messageBusy ? "Opening…" : "Message"}
              </button>
              <button
                onClick={requestQuote}
                className="rounded-full border border-slate-200 px-5 py-2.5 font-medium text-[#071b22] hover:bg-slate-50 transition-colors"
                data-testid="provider-request-quote"
              >
                Request quote
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-10">
          {/* LEFT: bio & details */}
          <div className="lg:col-span-2 space-y-10">
            <section data-testid="provider-about">
              <SectionTitle>About</SectionTitle>
              <p translate={provider.full_bio || provider.short_intro ? "no" : undefined} className="text-[#252A34]/85 leading-relaxed whitespace-pre-line">
                {provider.full_bio || provider.short_intro || "This pro hasn't added a bio yet."}
              </p>
            </section>

            <section data-testid="provider-services">
              <SectionTitle>Categories</SectionTitle>
              <div className="flex flex-wrap gap-2">
                {(provider.category_ids || []).map((id) => (
                  <Badge key={id} variant="outline" className="rounded-full bg-[#165a71]/5 border-[#165a71]/20 text-[#165a71]">
                    {categoryMap[id]?.name || id}
                  </Badge>
                ))}
                {(provider.subcategory_ids || []).map((id) => (
                  <Badge key={id} variant="outline" className="rounded-full bg-slate-50 border-slate-200 text-[#252A34]/80">
                    {categoryMap[id]?.name || id}
                  </Badge>
                ))}
                {(provider.skills || []).map((s) => (
                  <Badge key={s} variant="outline" className="rounded-full border-slate-200 text-[#252A34]/70 capitalize">
                    {s}
                  </Badge>
                ))}
              </div>
            </section>

            {services.length > 0 && (
              <section data-testid="provider-services-list">
                <SectionTitle>
                  Services offered <span className="text-[#252A34]/60 text-base font-normal">· {services.length}</span>
                </SectionTitle>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {services.map((s, i) => <ServiceCard key={s.id} service={s} index={i} />)}
                </div>
              </section>
            )}

            {provider.gallery?.length > 0 && (
              <section>
                <SectionTitle>Portfolio</SectionTitle>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {provider.gallery.map((url, i) => (
                    <img key={i} src={url} alt="" className="w-full h-40 object-cover rounded-xl" />
                  ))}
                </div>
              </section>
            )}

            <section data-testid="provider-reviews-section">
              <SectionTitle>
                Reviews {reviews.length > 0 && <span className="text-[#252A34]/60 text-base font-normal">· {reviews.length}</span>}
              </SectionTitle>
              {reviews.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-6 text-sm text-[#252A34]/70">
                  No reviews yet — be the first to hire this pro.
                </div>
              ) : (
                <div className="grid gap-3">
                  {reviews.map((r) => <ReviewCard key={r.id} review={r} />)}
                </div>
              )}
            </section>

            {webReviews.length > 0 && (
              <section data-testid="provider-web-reviews-section">
                <SectionTitle>Reviews from the web</SectionTitle>
                <WebReviews items={webReviews} />
              </section>
            )}
          </div>

          {/* RIGHT: at-a-glance */}
          <aside className="space-y-6" data-testid="provider-sidebar">
            <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4">
              <SectionTitle small>At a glance</SectionTitle>
              <Row label="Experience" value={`${provider.years_experience || 0}+ years`} />
              <Row label="Type" value={provider.provider_type} />
              <Row label="Structure" value={provider.provider_variant} />
              {provider.service_radius_miles && (
                <Row label="Service radius" value={`${provider.service_radius_miles} mi`} />
              )}
              <Row label="Response rate" value={`${Math.round((provider.response_rate || 0) * 100)}%`} />
              {provider.website && (
                <Row label="Website" value={<a href={provider.website} className="text-[#165a71] hover:underline break-all" target="_blank" rel="noreferrer">{provider.website}</a>} />
              )}
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="provider-verifications">
              <SectionTitle small>Verifications</SectionTitle>
              <div className="mt-3 space-y-2.5 text-sm">
                {Object.entries(provider.verifications || {}).map(([level, meta]) => (
                  <div key={level} className="flex items-center justify-between">
                    <span className="text-[#252A34]/80 capitalize">{level.replace(/_/g, " ")}</span>
                    {meta.status === "approved" ? (
                      <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-medium">
                        <CheckCircle2 className="h-4 w-4" /> approved
                      </span>
                    ) : (
                      <span className="text-xs text-[#252A34]/60 capitalize">{meta.status?.replace(/_/g, " ")}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-6">
              <SectionTitle small>Serves</SectionTitle>
              <div className="mt-3 flex flex-wrap gap-2">
                {(provider.location_ids || []).map((id) => (
                  <Badge key={id} variant="outline" className="rounded-full border-slate-200 text-[#252A34]/80 inline-flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    {locationMap[id]?.name || id}
                  </Badge>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </div>
      <TemplateBands entity="providers" slot="footer" />
    </PublicLayout>
  );
}

const SectionTitle = ({ children, small }) => (
  <div className={`font-display text-[#071b22] font-semibold mb-4 ${small ? "text-sm uppercase tracking-wider text-[#252A34]/60" : "text-xl md:text-2xl"}`}>
    {children}
  </div>
);
const Row = ({ label, value }) => (
  <div className="flex items-center justify-between text-sm">
    <span className="text-[#252A34]/60">{label}</span>
    <span className="text-[#071b22] font-medium capitalize text-right">{value}</span>
  </div>
);
